import { router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { csrfFetch } from "@/lib/csrf-fetch"
import type { ReportViewConfig, ReportViewData } from "@/types"

interface Props {
  open: boolean
  onOpenChange: (next: boolean) => void
  /** Initial config to save — usually the page's current state. */
  config: ReportViewConfig
  /** Existing views (for client-side name-collision check). */
  existingViews: ReportViewData[]
  /** Path used to POST: /orgs/:slug/reports/:report_id/views */
  createPath: string
  /** Where to navigate after a successful save, including ?view_id=NEW. */
  buildSuccessPath: (newViewId: number) => string
  captureWarning?: string
}

const MAX_NAME_LENGTH = 80

// Three locked validation states (per Locked Visual Spec / D3):
//   empty       → "Give your view a name to save it."
//   too long    → "Name is too long (80 char max). You typed N."
//   collision   → "You already have a view named '{name}'..."
//
// The submit button is disabled until the input is non-empty AND under
// the length cap. Collision is checked client-side first (we have the
// list of existing names); the server is the final authority (race-safe
// + same validator the model uses).
export function SaveViewModal({
  open,
  onOpenChange,
  config,
  existingViews,
  createPath,
  buildSuccessPath,
  captureWarning,
}: Props) {
  const [name, setName] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName("")
      setServerError(null)
    }
  }, [open])

  const trimmed = name.trim()
  const lengthError =
    trimmed.length > MAX_NAME_LENGTH
      ? `Name is too long (${MAX_NAME_LENGTH} char max). You typed ${trimmed.length}.`
      : null
  const collisionError = existingViews.some(
    (v) => v.name.toLowerCase() === trimmed.toLowerCase(),
  )
    ? `An organization view named "${trimmed}" already exists. Choose another name.`
    : null
  const emptyError =
    name.length > 0 && trimmed.length === 0
      ? "Give your view a name to save it."
      : null

  const error = serverError || lengthError || collisionError || emptyError
  const canSubmit =
    !submitting && trimmed.length > 0 && !lengthError && !collisionError

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setServerError(null)
    try {
      const res = await csrfFetch(createPath, {
        method: "POST",
        body: JSON.stringify({ view: { name: trimmed, config } }),
      })
      if (res.ok) {
        const body = (await res.json()) as {
          id?: number
          name?: string
          view?: { id: number; name: string }
        }
        const created = body.view ?? body
        if (!created.id)
          throw new Error("The saved view response was incomplete.")
        toast.success(`Saved view "${created.name ?? trimmed}"`)
        onOpenChange(false)
        router.visit(buildSuccessPath(created.id), { preserveScroll: true })
      } else {
        const body = (await res
          .json()
          .catch(() => ({ errors: ["Save failed."] }))) as {
          errors?: string[]
        }
        setServerError(body.errors?.join(", ") ?? "Save failed.")
      }
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : "Network error — try again.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!submitting || next) onOpenChange(next)
      }}
    >
      <DialogContent className="grid max-h-[90vh] max-w-[calc(100%-2rem)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden p-0 **:data-[slot=dialog-close]:flex **:data-[slot=dialog-close]:size-11 **:data-[slot=dialog-close]:items-center **:data-[slot=dialog-close]:justify-center sm:max-w-lg">
        <form
          onSubmit={handleSubmit}
          className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)_auto]"
        >
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>Save organization view</DialogTitle>
            <DialogDescription>
              Your organization can use this view. Editors can update or delete
              it later.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 space-y-2 overflow-y-auto px-6 py-5">
            <Label htmlFor="save-view-name">Name</Label>
            <Input
              id="save-view-name"
              autoFocus
              placeholder="e.g. Monthly customer review"
              value={name}
              maxLength={MAX_NAME_LENGTH + 20}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={
                Boolean(error) && error !== emptyError ? true : undefined
              }
              aria-describedby={error ? "save-view-error" : undefined}
              className={
                error && error !== emptyError
                  ? "ring-destructive h-11 ring-2 ring-offset-0"
                  : "h-11"
              }
            />
            {error && (
              <p
                id="save-view-error"
                role="alert"
                className={
                  error === emptyError
                    ? "text-muted-foreground text-xs"
                    : "text-destructive text-xs"
                }
              >
                {error}
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {trimmed.length}/{MAX_NAME_LENGTH}
            </p>
            {captureWarning && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {captureWarning}
              </p>
            )}
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="h-11" disabled={!canSubmit}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save organization view
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
